import { useState, useEffect } from 'react'
import { type GQLError, type DefaultContext } from '@egw/clients'

interface UseQueryResult<TResponse> {
  isLoading: boolean
  error: GQLError | null
  isSuccess: boolean
  data: TResponse | null
}

function useQuery<TInput, TResponse>(
  execute: (_input?: TInput, _context?: DefaultContext) => Promise<TResponse>,
  input?: TInput,
): UseQueryResult<TResponse> {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<GQLError | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)
  const [data, setData] = useState<TResponse | null>(null)

  useEffect(() => {
    const query = async (): Promise<TResponse | undefined> => {
      setIsLoading(true)
      if (error) setError(null)
      setIsSuccess(false)
      try {
        const response = await execute(input)
        setData(response)
        setIsSuccess(true)
        return response
      } catch (err) {
        setError(err as GQLError)
        setIsSuccess(false)
      } finally {
        setIsLoading(false)
      }
    }
    void query()
  }, [])

  return { isLoading, error, isSuccess, data }
}

/**
 * @param TInput - input type
 * @param TResponse - response type
 */
export const createQueryHook = <TInput, TResponse>(
  query: (_input?: TInput, _context?: DefaultContext) => Promise<TResponse>,
) => {
  return (input?: TInput) => useQuery<TInput, TResponse>(query, input)
}
